/* ═══════════════════════════════════════════════════════════════════════════
   THE PEOPLE

   Until now a member of staff was `{name, role}` — two strings, enough to put
   a name under a time slot and nothing else. That is the gap behind "agents'
   full info": the prototype's people are the thing a customer actually
   chooses. You do not book "the salon", you book Marte at half past two; you
   do not instruct "the firm", you instruct Ingrid because she takes the case
   to court and answers in twelve minutes.

   So this file carries the prototype's whole person record, and it carries it
   for all 172 people in the catalogue rather than for the nine advisers it
   was written for. One shape for everybody:

     who they are     name, initials, portrait, title, years, languages
     what they do     areas, tags, a bio in their own voice
     how you reach    channels (call / video / in person), direct extension,
                      the response time they actually keep
     when             status and the next free time, in words
     what it costs    only where the trade shows a price at all
     standing         for a regulated trade: the register, the label, and a
                      number that is NOT invented — see below

   ── Two rules this file keeps ──────────────────────────────────────────────

   **No invented registration numbers.** A practising certificate number, a
   Finanstilsynet approval number, an MNAL membership number: these are
   checkable facts about a real person, and a plausible-looking fake one is
   worse than none, because somebody will read it as checked. `registration`
   carries the body and the label — which are facts about the trade, not about
   the person — and `number: null` with `checked: false`. The register lookup
   fills it in when Chamcore is connected. The UI is written to say "not
   checked in this build" rather than to hide the row.

   **The nine advisers are ported, not generated.** a1–a9 in the prototype are
   Stein's own writing: the bios, the response times, the insurance notes. They
   are copied across word for word, translated only where the Norwegian was
   ordinary English ("Ledig nå" → "Free now"), and never where it is the
   product's own vocabulary or a Norwegian body's name. Everybody else is
   derived, deterministically, from their name and role — the same FNV-1a hash
   that deals the photographs out, so a person's profile does not change
   between two loads of the same page.
   ═══════════════════════════════════════════════════════════════════════ */
import { STAFF, BUSINESSES } from './catalogue.js';
import { industry, ADVISORY } from '../industries/index.js';

/* ── The hash ──────────────────────────────────────────────────────────────
   FNV-1a, the same one in images/index.js. Deterministic and spread evenly
   over small inputs, which is all that is wanted: a stable pick per person
   rather than a random one per render. */
function hash(s) {
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 0x01000193) >>> 0;
  }
  return h >>> 0;
}
const pick = (list, seed) => list[hash(seed) % list.length];
const between = (seed, lo, hi) => lo + (hash(seed) % (hi - lo + 1));

const initials = name =>
  name.split(/\s+/).filter(Boolean).slice(0, 2).map(w => w[0]).join('').toUpperCase();

/* The four portraits that came out of the prototype. There are four, and 172
   people: they are given to the advisers the prototype gave them to, and to
   nobody else. A stock face on a hairdresser nobody has photographed is a
   lie the picture tells on its own, and the initials block is honest. */
const PORTRAIT = n => `${(import.meta.env?.BASE_URL || '/').replace(/\/?$/, '/')}images/_people/portrait-${n}.webp`;

/* ── The nine, ported ─────────────────────────────────────────────────────
   Field for field from `advisors` in the prototype. `mode` is how the money
   works and it is the prototype's word: hourly, per_minute, fixed, quote. */
const PORTED = [
  {
    id: 'a1', business: 'hovden', name: 'Ingrid Hovden', portrait: PORTRAIT(3),
    title: 'Advokat MNA, partner', premium: true, place: 'Oslo',
    areas: ['Family & children', 'Inheritance & probate'],
    tags: ['Barnefordeling', 'Samlivsbrudd', 'Testament'],
    mode: 'hourly', rate: 1890, intro: 15, rating: 4.9, reviews: 128, years: 12,
    response: '12 min', languages: ['NO', 'EN'], status: 'on',
    available: 'Free now', available_sub: 'Answers the phone',
    channels: { call: true, video: true, meet: true }, extension: '1115',
    bio: 'Litigates family cases before the district court and the court of appeal. Appointed counsel for the aggrieved since 2019. I take cases all the way — not only as far as an attempt to settle.',
    insurance: 'Legal-expenses cover in home insurance often pays 80 % up to 100 000 kr.',
  },
  {
    id: 'a4', business: 'hovden', name: 'Fredrik Aune', portrait: PORTRAIT(1),
    title: 'Advokatfullmektig', premium: false, place: 'Oslo',
    areas: ['Immigration', 'Contract'],
    tags: ['Oppholdstillatelse', 'Familiegjenforening'],
    mode: 'quote', rate: 0, intro: 15, rating: 4.7, reviews: 29, years: 3,
    response: '2 h', languages: ['NO', 'EN', 'AR'], status: 'off',
    available: 'Book a time', available_sub: 'Next: Thursday',
    channels: { call: false, video: true, meet: true }, extension: '1142',
    bio: 'A fixed price is agreed after a free assessment of the case. Authorised trainee under advokat Ingrid Hovden.',
    insurance: '',
  },
  {
    id: 'a2', business: 'sandberg', name: 'Marius Sandberg', portrait: PORTRAIT(2),
    title: 'Advokat MNA, daglig leder', premium: true, place: 'Digitalt',
    areas: ['Employment', 'Contract'],
    tags: ['Oppsigelse', 'Nedbemanning', 'Sluttavtale'],
    mode: 'per_minute', rate: 39, intro: 0, rating: 4.8, reviews: 341, years: 9,
    response: 'at once', languages: ['NO', 'EN', 'DE'], status: 'on',
    available: 'Free now', available_sub: 'Direct line open',
    channels: { call: true, video: true, meet: false }, extension: '1137',
    bio: 'Advice by telephone with no commitment — you pay only for the minutes you use. If it becomes a case, the direct line moves to a fixed hourly rate agreed in writing.',
    insurance: 'A trade union often covers legal help in a dismissal — check the membership.',
  },
  {
    id: 'a8', business: 'sandberg', name: 'Nora Fjeld', portrait: PORTRAIT(4),
    title: 'Advokat MNA', premium: true, place: 'Digitalt',
    areas: ['Contract', 'Property'],
    tags: ['Forbrukerkjøp', 'Håndverkertvist', 'Boligkjøp'],
    mode: 'hourly', rate: 1490, intro: 15, rating: 4.9, reviews: 145, years: 15,
    response: '20 min', languages: ['NO', 'EN'], status: 'on',
    available: 'Free now', available_sub: 'Answers the phone',
    channels: { call: true, video: true, meet: false }, extension: '1139',
    bio: 'Complaints after a house purchase and disputes with a tradesman. I always start by checking whether your insurance covers the case.',
    insurance: 'Home-buyer and home-contents insurance often cover the fee in whole or in part.',
  },
  {
    id: 'a3', business: 'nordvik', name: 'Kaja Lindberg', portrait: null,
    title: 'Advokat MNA', premium: true, place: 'Bergen',
    areas: ['Inheritance & probate', 'Property'],
    tags: ['Dødsbo', 'Skifte', 'Uskifte'],
    mode: 'fixed', rate: 990, fixed_min: 30, intro: 0, rating: 5.0, reviews: 64, years: 16,
    response: '1 h', languages: ['NO'], status: 'soon',
    available: 'Free today 14:30', available_sub: '3 free times',
    channels: { call: true, video: true, meet: true }, extension: '1138',
    bio: 'A fixed price on the first consultation: thirty minutes, a concrete answer and a written summary afterwards.',
    insurance: '',
  },
  {
    id: 'a9', business: 'nordvik', name: 'Eirik Nordvik', portrait: null,
    title: 'Advokat MNA, partner', premium: false, place: 'Trondheim',
    areas: ['Company law', 'Criminal', 'Employment'],
    tags: ['Prosedyre', 'Styreansvar', 'Økonomisk kriminalitet'],
    mode: 'hourly', rate: 2150, intro: 0, rating: 4.6, reviews: 73, years: 27,
    response: '3 h', languages: ['NO', 'EN'], status: 'soon',
    available: 'Free Friday', available_sub: '2 free times',
    channels: { call: true, video: true, meet: true }, extension: '1144',
    bio: 'Right of audience before the Supreme Court. Litigates company-law disputes and cases on directors’ liability.',
    insurance: '',
  },
  {
    id: 'a5', business: 'bakke', name: 'Trine Bakke', portrait: null,
    title: 'Statsautorisert revisor', premium: true, place: 'Oslo',
    areas: ['Company law', 'Year-end', 'Valuation'],
    tags: ['Årsoppgjør', 'Fisjon', 'Verdivurdering'],
    mode: 'hourly', rate: 1450, intro: 20, rating: 4.9, reviews: 87, years: 18,
    response: '40 min', languages: ['NO', 'EN'], status: 'on',
    available: 'Free now', available_sub: 'Answers the phone',
    channels: { call: true, video: true, meet: true }, extension: '1150',
    bio: 'Audit and advice for limited companies with 1–50 employees. The same contact person the whole year round.',
    insurance: '',
  },
  {
    id: 'a6', business: 'vold', name: 'Henrik Vold', portrait: null,
    title: 'Sivilarkitekt MNAL', premium: true, place: 'Oslo',
    areas: ['Property', 'Planning application', 'Zoning', 'Extension'],
    tags: ['Byggesøknad', 'Tilbygg', 'Regulering'],
    mode: 'quote', rate: 0, intro: 20, rating: 4.8, reviews: 52, years: 11,
    response: '3 h', languages: ['NO'], status: 'soon',
    available: 'Free tomorrow', available_sub: 'Site visit possible',
    channels: { call: true, video: true, meet: true }, extension: '1161',
    bio: 'From sketch to an approved planning application. A quote is given after a short site visit or a video meeting.',
    insurance: '',
  },
  {
    id: 'a7', business: 'ravn', name: 'Sofie Ravn', portrait: null,
    title: 'Rådgivende ingeniør', premium: true, place: 'Oslo',
    areas: ['Structure', 'Fire', 'Energy', 'Property'],
    tags: ['Bæreevne', 'Brannkonsept', 'Energiberegning'],
    mode: 'hourly', rate: 1290, intro: 15, rating: 4.9, reviews: 41, years: 14,
    response: '25 min', languages: ['NO', 'EN'], status: 'on',
    available: 'Free now', available_sub: 'Answers the phone',
    channels: { call: true, video: true, meet: true }, extension: '1174',
    bio: 'Structural and fire-engineering advice for housing projects and smaller commercial buildings.',
    insurance: '',
  },
];

/* ── Deriving the other 163 ───────────────────────────────────────────────
   Everything below is generated, and generated the same way every time. The
   pieces are the ones a customer reads before choosing a person: how long
   they have done it, what they are known for, when they are next free, and
   how quickly they answer. Nothing here claims to be checked. */

/* What a person of this role is known for. Keyed on the Norwegian role in the
   catalogue, because that is the word the business itself uses. */
const SPECIALISMS = {
  'Frisør':           ['Cut', 'Blow-dry', 'Long hair', 'Curls', 'Fringe', 'Children'],
  'Barber':           ['Fade', 'Beard', 'Razor shave', 'Skin fade', 'Classic cut'],
  'Fargespesialist':  ['Balayage', 'Highlights', 'Colour correction', 'Toning', 'Full colour'],
  'Behandler':        ['Facial', 'Peeling', 'Lash', 'Brow', 'Skin analysis'],
  'Terapeut':         ['Deep tissue', 'Sports massage', 'Shoulders & neck', 'Pregnancy', 'Trigger point'],
  'Instruktør':       ['Strength', 'Mobility', 'Beginners', 'Vinyasa', 'Rehab', 'Intervals'],
  'Tekniker':         ['Diagnosis', 'Callout', 'Service', 'Fitting', 'Damage control'],
  'Fagperson':        ['Inspection', 'Assessment', 'Repair', 'Maintenance'],
  'Renholder':        ['Deep clean', 'Move-out', 'Windows', 'Regular'],
  'Vert':             ['Dining room', 'Groups', 'Wine', 'Allergies'],
  'Rådgiver':         ['Advice', 'Assessment', 'Documentation'],
  'Advokat':          ['Litigation', 'Contract', 'Negotiation'],
  'Arkitekt':         ['Sketch', 'Planning application', 'Extension'],
};

const LANGS = [['NO'], ['NO'], ['NO', 'EN'], ['NO', 'EN'], ['NO', 'EN', 'PL'], ['NO', 'EN', 'AR']];
const RESPONSES = ['10 min', '15 min', '25 min', '40 min', '1 h', '2 h'];
const FREE_SOON = [
  ['Free now', 'Answers the phone'],
  ['Free now', 'Walk in today'],
  ['Free today 14:30', '3 free times'],
  ['Free tomorrow', '5 free times'],
  ['Free Friday', '2 free times'],
];

/* The bio. A sentence about what they do and a sentence about how they work,
   both assembled from the role — which is honest about being a demonstration
   and reads as a person rather than as lorem ipsum. */
function bioFor(name, role, code, specialisms) {
  const w = industry(code)?.words || {};
  const first = name.split(' ')[0];
  const opener = pick([
    `${specialisms[0]} and ${String(specialisms[1] || specialisms[0]).toLowerCase()} are what I do most of.`,
    `I have spent most of my working life on ${String(specialisms[0]).toLowerCase()}.`,
    `Most of my week is ${String(specialisms[0]).toLowerCase()}.`,
  ], name + '|open');
  const manner = pick([
    'I would rather say what will not work than sell you something that will not.',
    'We agree what it is going to be before we start, and what it will cost.',
    `New ${w.customers ? String(w.customers).toLowerCase() : 'customers'} get a proper first conversation — there is no charge for that.`,
    'Book with me directly if you want the same person each time.',
  ], name + '|manner');
  return `${opener} ${manner}`.replace(/\s+/g, ' ');
}

/* The regulated trades. The body and the label are facts about the trade and
   are safe to state; the number is not ours to make up. */
const REGISTERS = {
  legal:      { body: 'Advokattilsynet', label: 'Advokatbevilling' },
  accounting: { body: 'Finanstilsynet',  label: 'Godkjent revisor' },
  architect:  { body: 'NAL',             label: 'MNAL' },
  engineer:   { body: 'RIF',             label: 'RIF-godkjent' },
  health:     { body: 'Helsedirektoratet', label: 'Autorisert helsepersonell' },
};

function derive(businessId, code, entry, index) {
  const seed = businessId + '|' + entry.name;
  const bank = SPECIALISMS[entry.role] || SPECIALISMS['Fagperson'];
  /* The first entry in each bank is the broad one — the thing everybody in
     that role does — and it leads, because "most of my week is fringe" is not
     a sentence a hairdresser writes. The one or two after it are the ones that
     distinguish this person, and they are dealt from the rest of the bank so
     two people in the same shop do not read identically. */
  const rest = bank.slice(1);
  const start = hash(seed) % rest.length;
  const many = 1 + (hash(seed + '|n') % 2);
  const tags = [bank[0], ...Array.from({ length: many }, (_, i) => rest[(start + i) % rest.length])];
  const [available, available_sub] = pick(FREE_SOON, seed + '|free');

  return {
    id: businessId + '-' + (index + 1),
    business: businessId,
    name: entry.name,
    portrait: null,
    title: entry.role,
    premium: false,
    place: null,
    areas: tags.slice(0, 2),
    tags,
    mode: null, rate: 0, intro: 0,
    rating: Number((4.5 + (hash(seed + '|r') % 5) / 10).toFixed(1)),
    reviews: between(seed + '|rev', 8, 160),
    years: between(seed + '|y', 1, 24),
    response: pick(RESPONSES, seed + '|resp'),
    languages: pick(LANGS, seed + '|lang'),
    status: available.startsWith('Free now') ? 'on' : 'soon',
    available, available_sub,
    /* A video meeting is offered where the trade's work can actually be
       done down a camera — which is what `listing: 'advisor'` means on the
       registry. A haircut cannot, so it is not offered. */
    channels: { call: true, video: ADVISORY.includes(code), meet: true },
    extension: String(1200 + (hash(seed + '|dial') % 700)),
    bio: bioFor(entry.name, entry.role, code, tags),
    insurance: '',
  };
}

/* ── Assembly ─────────────────────────────────────────────────────────────── */

const BY_BUSINESS = {};
const BY_ID = {};

for (const b of BUSINESSES) {
  const ported = PORTED.filter(p => p.business === b.id);
  const roster = ported.length
    ? ported
    : (STAFF[b.id] || []).map((entry, i) => derive(b.id, b.industry, entry, i));

  const reg = REGISTERS[b.industry] || null;
  const full = roster.map(p => ({
    ...p,
    industry: b.industry,
    business_name: b.name,
    initials: p.initials || initials(p.name),
    place: p.place || b.city || null,
    /* The register, stated; the number, not invented. */
    registration: reg ? { ...reg, number: null, checked: false } : null,
  }));

  BY_BUSINESS[b.id] = full;
  full.forEach(p => { BY_ID[p.id] = p; });
}

/** Everyone who works at a business, with their whole record. */
export function peopleAt(businessId) {
  return BY_BUSINESS[businessId] || [];
}

/** One person, by id. */
export function person(id) {
  return BY_ID[id] || null;
}

/** Everybody, for the teaser and for search. */
export const PEOPLE = Object.values(BY_ID);

/** The regulated trades, so a surface can ask rather than hold a list. */
export const REGULATED = Object.keys(REGISTERS);

/** How the money works, in words. Returns null where the trade does not show
    a price — the industry registry, not this file, decides that. */
export function rateWords(p) {
  if (!p || !p.mode) return null;
  const kr = n => n.toLocaleString('nb-NO') + ' kr';
  if (p.mode === 'hourly')     return { price: 'from ' + kr(p.rate), unit: 'per hour' };
  if (p.mode === 'per_minute') return { price: kr(p.rate) + '/min', unit: 'no starting fee' };
  if (p.mode === 'fixed')      return { price: kr(p.rate), unit: `fixed · ${p.fixed_min} min` };
  if (p.mode === 'quote')      return { price: 'By quote', unit: 'after a free assessment' };
  return null;
}
